import AIcon from '@/components/A/AIcon/AIcon'
import styles from './ExperienceProjectDetailPage.module.less'
import MiddleText from '@/components/MiddleText/MiddleText'
import cls from 'classnames'
import InfoDrawerBtn from '../ProjectDetailPage/components/InfoDrawerBtn/InfoDrawerBtn'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import useNewNavigate from '@/helpers/commonHooks/useNewNavigate'
import AnalysisFlowView from './components/AnalysisFlowView/AnalysisFlowView'
import AnalysisPPTView from './components/AnalysisPPTView/AnalysisPPTView'
import { commonUtils } from '@/helpers/commonUtils'
import AButton from '@/components/A/AButton/AButton'
import { useLocation } from 'react-router-dom'
import { useAutoRequest } from 'alova/client'
import { api } from '@/api'
import { message } from 'antd'
import useProductType from '@/helpers/commonHooks/useProductType'
import { getSafeFactorScore, SCORE_PLACEHOLDER } from './components/AnalysisFlowView/scoreUtils'
import { getProductDashboardLink } from '@/router/pageConst'
import { isPortraitProductCategory, isQuickProductCategory } from '@/helpers/productCategory'

interface Props {
  onProjectDetailUpdate: (project: ProjectModels.ProjectDetailV3) => void
}

function normalizeExperienceProjectDetail(project: ProjectModels.ProjectDetailV3): ProjectModels.ProjectDetailV3 {
  return {
    ...project,
    detailMetrics: Array.isArray(project.detailMetrics) ? project.detailMetrics : [],
    frameInfos: Array.isArray(project.frameInfos) ? project.frameInfos : [],
    pptOutput: Array.isArray(project.pptOutput) ? project.pptOutput : [],
    heatmapResults: {
      heatmapImages: Array.isArray(project.heatmapResults?.heatmapImages) ? project.heatmapResults.heatmapImages : [],
      heatmapVideo: project.heatmapResults?.heatmapVideo ?? '',
    },
    shouldHideFreeTrialPpt: Boolean(project.shouldHideFreeTrialPpt),
  }
}


function ExperienceProjectDetailPage(props: Props) {
  const { t } = useTranslation()
  const FLOW_ANALYSIS_PREFIX = 'dashboard.experience.projectDetailPage.flowAnalysis'
  const { productType } = useProductType()
  const showPptView = !isQuickProductCategory(productType)
  const location = useLocation()
  const [fullscreenView, setFullscreenView] = useState<'flow' | 'ppt' | 'none'>('none')
  const canAutoChangeFullscreenView = useRef(true)
  const navigate = useNewNavigate()
  const [project, setProject] = useState<ProjectModels.ProjectDetailV3>()
  const prevProjectStatusRef = useRef<ProjectModels.ProjectDetailV3['status']>()
  const shouldPollingRef = useRef(true)
  const { data, send } = useAutoRequest(() => api.project.projectDetailV3(location.state.id), {
    pollingTime: 1000,
    middleware: (_, next) => {
      if (shouldPollingRef.current) {
        next()
      }
    },
  })

  const [flowViewIsFinish, setFlowViewIsFinish] = useState<boolean>(false)
  const adeffIndex = getSafeFactorScore(project?.dimensionFactors?.adeffIndex)
  const adeffResultText = adeffIndex.isComparable
    ? (adeffIndex.score! >= adeffIndex.meanScore!
      ? t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.pass`)
      : t(`${FLOW_ANALYSIS_PREFIX}.terminology.result.notPass`))
    : SCORE_PLACEHOLDER

  const analysisCompletedMessage = (() => {
    if (isQuickProductCategory(productType)) {
      return isPortraitProductCategory(productType)
        ? t('dashboard.projectPage.message.portraitQuickCompleted')
        : t('dashboard.projectPage.message.landscapeQuickCompleted')
    }
    if (isPortraitProductCategory(productType)) {
      return t('dashboard.projectPage.message.portraitSurveyCompleted')
    }
    return t('dashboard.projectPage.message.landscapeSurveyCompleted')
  })()


  const handleProjectDetailUpdate = async () => {
    shouldPollingRef.current = true
    const { data } = await send()
    shouldPollingRef.current = false
    props.onProjectDetailUpdate(data)
  }

  const handleReturn = () => {
    navigate(location.state?.returnTo || getProductDashboardLink(productType, 'project'))
  }

  const handleFullscreenClick = (view: 'flow' | 'ppt', isFullscreen: boolean) => {
    if (isFullscreen) {
      setFullscreenView(view)
    } else {
      setFullscreenView('none')
    }
  }

  const handleRetry = () => {
    if (!project) {
      return
    }
    api.project
      .projectRetry(project.id)
      .send()
      .then(() => {
        shouldPollingRef.current = true
        const nextProject = normalizeExperienceProjectDetail({
          ...project,
          status: 'waiting',
          errorMsg: '',
          taskId: '',
          progressRatio: 0,
          score: 0,
          meanScore: 0,
        })
        prevProjectStatusRef.current = nextProject.status
        canAutoChangeFullscreenView.current = true
        setFlowViewIsFinish(false)
        setProject(nextProject)
        props.onProjectDetailUpdate(nextProject)
        send()
      }).catch((e) => {
        const error = e as ApiError
        if (error.statusCode === 40021) {
          message.error(t('dashboard.projectPage.message.analysisCountNotEnough'))
          return
        }
        message.error(t('dashboard.projectPage.message.analysisFailed'))
      })
  }


  useEffect(() => {
    if (data) {
      if (prevProjectStatusRef.current && prevProjectStatusRef.current !== 'completed' && data.data.status === 'completed') {
        /** 如果上一次项目状态不是完成，这次项目状态是完成，则提示分析完成 */
        message.success(analysisCompletedMessage)
      }
      const _project = normalizeExperienceProjectDetail(data.data)
      shouldPollingRef.current = _project.status !== 'completed' && _project.status !== 'failed'
      prevProjectStatusRef.current = _project.status
      setProject(_project)
      if (_project.status === 'completed' || _project.status === 'failed') {
        props.onProjectDetailUpdate(_project)
      }
    }
  }, [analysisCompletedMessage, data])

  useEffect(() => {
    if (project && project.status !== 'failed') {
      const needChangeFullscreenView = flowViewIsFinish && fullscreenView === 'flow'
      if (needChangeFullscreenView && canAutoChangeFullscreenView.current) {
        handleFullscreenClick('flow', false)
      }
      if (project.progressRatio < 85 && canAutoChangeFullscreenView.current) {
        handleFullscreenClick('flow', true)
      }
    }
  }, [project, fullscreenView, flowViewIsFinish])

  return (
    <div className={styles.experienceProjectDetailPage} data-testid='project-detail-page'>
      {
        project && (
          <>
            <div className={styles.returnBox}>
              <div className={styles.returnBoxInner}>
                <div className={styles.returnBtn} onClick={handleReturn} data-testid='project-detail-return-btn'>
                  <span className={styles.iconBox}>
                    <AIcon type='icon-line-global_left' width={14} height={14}></AIcon>
                  </span>
                  <span className={styles.btnText}>
                    <MiddleText text={t('dashboard.projectDetailPage.testResults')} />
                  </span>
                </div>
                {
                  (project.status === 'analyzing' || project.status === 'creating') && (
                    <div className={cls(styles.badge, styles.ongoing)}>
                      {t('dashboard.projectDetailPage.ongoing')}
                    </div>
                  )
                }
                {
                  project.status === 'failed' && (
                    <div className={cls(styles.badge, styles.failed)}>
                      {t('dashboard.projectDetailPage.failed')}
                    </div>
                  )
                }
                {
                  project.status === 'waiting' && (
                    <div className={cls(styles.badge, styles.waiting)}>
                      {t('dashboard.projectDetailPage.waiting')}
                    </div>
                  )
                }
                {
                  project.status === 'completed' && (
                    <div className={cls(styles.badge, {
                      [styles.pass]: adeffIndex.isComparable && adeffIndex.score! >= adeffIndex.meanScore!,
                      [styles.notPass]: adeffIndex.isComparable && adeffIndex.score! < adeffIndex.meanScore!
                    })}>
                      <MiddleText text={adeffResultText} />
                    </div>
                  )
                }
              </div>
              <InfoDrawerBtn mode='default' project={project} onProjectUpdate={handleProjectDetailUpdate} />
            </div>
            <div className={styles.analysisContent}>
              {
                project.status === 'failed' && (
                  <div className={styles.failedBox}>
                    <img
                      style={{ width: 140 }}
                      src={commonUtils.getImagePath('projectPage/project-list-empty.png')}
                      alt=''
                    />
                    <p>{t('dashboard.projectDetailPage.videoAnalysisFailed')}</p>
                    <AButton testId='project-detail-retry-btn' type='outlined' shape='round' size='SM' onClick={handleRetry}>
                      {t('dashboard.projectDetailPage.tryAgain')}
                    </AButton>
                  </div>
                )
              }
              {
                project.status !== 'failed' && <>
                  <div className={cls(
                    styles.analysisFlowViewContainer,
                    fullscreenView === 'ppt' && styles.hidden,
                    (fullscreenView === 'flow' || !showPptView) && styles.fullScreen,
                  )}>
                    <AnalysisFlowView isFullscreen={fullscreenView === 'flow'} project={project}
                      canSwitch={showPptView}
                      canFullscreen={showPptView}
                      onClick={(isFullscreen) => {
                        canAutoChangeFullscreenView.current = false
                        handleFullscreenClick('flow', isFullscreen)
                      }}
                      onSwitch={() => {
                        canAutoChangeFullscreenView.current = false
                        handleFullscreenClick('ppt', true)
                      }}
                      onFinish={() => {
                        setFlowViewIsFinish(true)
                      }}
                    />
                  </div>
                  {showPptView && (
                    <div className={cls(
                      styles.analysisPptViewContainer,
                      fullscreenView === 'flow' && styles.hidden,
                    )}>
                      <AnalysisPPTView project={project} isFullscreen={fullscreenView === 'ppt'}
                        onClick={(isFullscreen) => {
                          canAutoChangeFullscreenView.current = false
                          handleFullscreenClick('ppt', isFullscreen)
                        }}
                        onSwitch={() => {
                          canAutoChangeFullscreenView.current = false
                          handleFullscreenClick('flow', true)
                        }}
                      />
                    </div>
                  )}
                </>
              }
            </div>
          </>
        )
      }
    </div>
  )
}

export default ExperienceProjectDetailPage
